import type { ColumnDef, RowSelectionState, SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { Columns3, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Callout } from '@/components/callouts/Callout';
import { DataTable, ExpandableText, OptionsDropdown } from '@/components/composed';
import { SeverityPill, SeverityPillColor } from '@/components/SeverityPill';
import { Button, Text, Tooltip } from '@/design-system/components/ui';
import { maskAddressValue } from '@/utils/format';

import {
  AffiliateApplication,
  AffiliateApplicationFormField,
  AffiliateApplicationStatus,
} from '../domain/affiliate-application-types';
import { FormResponseCell } from './FormResponseCell';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const SORTABLE_FIELDS = new Set(['created_at', 'status']);

type SortField = 'created_at' | 'updated_at' | 'status';

const getStatusColor = (status: AffiliateApplicationStatus): SeverityPillColor => {
  switch (status) {
    case AffiliateApplicationStatus.Approved:
      return 'success';
    case AffiliateApplicationStatus.Rejected:
      return 'error';
    case AffiliateApplicationStatus.OnHold:
      return 'warning';
    case AffiliateApplicationStatus.Pending:
    default:
      return 'info';
  }
};

const getStatusLabel = (status: AffiliateApplicationStatus): string => {
  switch (status) {
    case AffiliateApplicationStatus.Approved:
      return 'Approved';
    case AffiliateApplicationStatus.Rejected:
      return 'Rejected';
    case AffiliateApplicationStatus.OnHold:
      return 'On Hold';
    case AffiliateApplicationStatus.Pending:
    default:
      return 'Pending';
  }
};

const buildDetails = (application: AffiliateApplication): { label: string; value: React.ReactNode }[] => [
  {
    label: 'User',
    value: application.userIdentifier ? maskAddressValue(application.userIdentifier) : '-',
  },
  {
    label: 'Date',
    value: `${dayjs.utc(application.createdAt).format('MMM D, YYYY HH:mm:ss')} UTC`,
  },
];

interface Props {
  applications: AffiliateApplication[];
  formFields: AffiliateApplicationFormField[];
  isLoading: boolean;
  isError: boolean;
  selectedRows: string[];
  selectedStatus: AffiliateApplicationStatus | null;
  /** Replaces both `onRowSelection(id)` and `onSelectAll(checked)` — the parent
   * receives the full next set of selected ids. */
  onRowSelectionChange: (ids: string[]) => void;
  sortBy?: SortField;
  sortOrder?: 'asc' | 'desc';
  onSort?: (sortBy: SortField) => void;
  onRefresh: () => void;
  isRefetching: boolean;
  total?: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const ADMIN_NOTES_COLUMN_ID = 'admin_notes';
const REJECTION_REASON_COLUMN_ID = 'rejection_reason';

export const AffiliateApplicationsTable = ({
  applications,
  formFields,
  isLoading,
  isError,
  selectedRows,
  selectedStatus,
  onRowSelectionChange,
  sortBy,
  sortOrder,
  onSort,
  onRefresh,
  isRefetching,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: Props) => {
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());

  const isSelectable = (status: AffiliateApplicationStatus): boolean => {
    if (selectedStatus === null) return true;
    return status === selectedStatus;
  };

  const sortedFormFields = useMemo(() => [...formFields].sort((a, b) => a.order - b.order), [formFields]);

  const allFilterableColumns = useMemo(() => {
    const columns = [
      { id: ADMIN_NOTES_COLUMN_ID, label: 'Admin Notes' },
      { id: REJECTION_REASON_COLUMN_ID, label: 'Rejection Reason' },
      ...sortedFormFields.map((f) => ({ id: f.id, label: f.label })),
    ];
    return columns.sort((a, b) => a.label.localeCompare(b.label));
  }, [sortedFormFields]);

  const isAdminNotesVisible = !hiddenColumns.has(ADMIN_NOTES_COLUMN_ID);
  const isRejectionReasonVisible = !hiddenColumns.has(REJECTION_REASON_COLUMN_ID);

  const visibleFormFields = useMemo(
    () => sortedFormFields.filter((field) => !hiddenColumns.has(field.id)),
    [sortedFormFields, hiddenColumns],
  );

  const columns = useMemo<ColumnDef<AffiliateApplication, unknown>[]>(() => {
    const cols: ColumnDef<AffiliateApplication, unknown>[] = [
      {
        id: 'created_at',
        header: 'Date',
        meta: { cellClassName: 'tw:whitespace-nowrap' },
        cell: ({ row }) => `${dayjs.utc(row.original.createdAt).format('MMM D HH:mm:ss')} UTC`,
      },
      {
        id: 'user',
        header: 'User',
        enableSorting: false,
        cell: ({ row }) => (
          <Tooltip content={row.original.userIdentifier ?? ''}>
            <span>{row.original.userIdentifier ? maskAddressValue(row.original.userIdentifier) : '-'}</span>
          </Tooltip>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <SeverityPill color={getStatusColor(row.original.status)}>{getStatusLabel(row.original.status)}</SeverityPill>
        ),
      },
    ];

    if (isRejectionReasonVisible) {
      cols.push({
        id: 'rejection_reason',
        header: 'Rejection Reason',
        enableSorting: false,
        size: 340,
        meta: { align: 'left' },
        cell: ({ row }) => (
          <ExpandableText
            label="Rejection Reason"
            text={row.original.rejectionReason || '-'}
            details={buildDetails(row.original)}
          />
        ),
      });
    }

    if (isAdminNotesVisible) {
      cols.push({
        id: 'admin_notes',
        header: 'Admin Notes',
        enableSorting: false,
        size: 340,
        meta: { align: 'left' },
        cell: ({ row }) => (
          <ExpandableText
            label="Admin Notes"
            text={row.original.adminNotes || '-'}
            details={buildDetails(row.original)}
          />
        ),
      });
    }

    visibleFormFields.forEach((field) => {
      cols.push({
        id: `form_${field.id}`,
        header: field.label,
        enableSorting: false,
        size: 340,
        meta: { align: 'left' },
        cell: ({ row }) => (
          <FormResponseCell
            label={field.label}
            value={row.original.formResponses[field.id]}
            type={field.type}
            details={buildDetails(row.original)}
          />
        ),
      });
    });

    return cols;
  }, [isAdminNotesVisible, isRejectionReasonVisible, visibleFormFields]);

  const sortingState: SortingState = useMemo(() => {
    if (!sortBy || !SORTABLE_FIELDS.has(sortBy)) return [];
    return [{ id: sortBy, desc: sortOrder === 'desc' }];
  }, [sortBy, sortOrder]);

  const handleSortingChange = (next: SortingState) => {
    const nextField = next[0]?.id;
    if (!nextField || !onSort) return;
    if (nextField === 'created_at' || nextField === 'status') {
      onSort(nextField);
    }
  };

  const rowSelection: RowSelectionState = useMemo(
    () => Object.fromEntries(selectedRows.map((id) => [id, true])),
    [selectedRows],
  );

  const handleRowSelectionChange = (state: RowSelectionState) => {
    onRowSelectionChange(Object.keys(state).filter((k) => state[k]));
  };

  const totalCount = total ?? 0;
  const visibleCount = applications.length;
  const resultsLabel =
    selectedRows.length > 0
      ? `Selected ${selectedRows.length} ${total ? `of ${total} results` : 'results'}`
      : `Showing ${visibleCount} ${total ? `of ${total} results` : 'results'}`;

  if (isError && applications.length === 0 && !isLoading) {
    return (
      <div className="tw:pb-2">
        <Callout
          title="Something went wrong"
          type="error"
          body={
            <div className="tw:flex tw:flex-col tw:gap-4">
              <span>Could not load affiliate applications list</span>
              <Button size="sm" variant="outline" className="tw:w-fit" disabled={isRefetching} onClick={onRefresh}>
                <RefreshCw size={16} />
                Retry
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <DataTable<AffiliateApplication>
      columns={columns}
      data={applications}
      loading={isLoading}
      getRowId={(row) => row.id}
      enableRowSelection={(row) => isSelectable(row.original.status)}
      rowSelection={rowSelection}
      onRowSelectionChange={handleRowSelectionChange}
      manualPagination
      rowCount={totalCount}
      paginationState={{ pageIndex: Math.max(0, page - 1), pageSize }}
      onPaginationChange={(next) => {
        if (next.pageSize !== pageSize) {
          onPageSizeChange(next.pageSize);
          return;
        }
        if (next.pageIndex !== page - 1) {
          onPageChange(next.pageIndex + 1);
        }
      }}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      initialSorting={sortingState}
      onSortingChange={handleSortingChange}
      onRowClick={(row) => {
        if (!isSelectable(row.status)) return;
        const next = selectedRows.includes(row.id)
          ? selectedRows.filter((id) => id !== row.id)
          : [...selectedRows, row.id];
        onRowSelectionChange(next);
      }}
      resultsLabel={resultsLabel}
      onRefresh={onRefresh}
      isRefreshing={isRefetching}
      actions={
        allFilterableColumns.length > 0 ? (
          <OptionsDropdown
            options={allFilterableColumns}
            value={new Set(allFilterableColumns.filter((c) => !hiddenColumns.has(c.id)).map((c) => c.id))}
            onValueChange={(next) => {
              setHiddenColumns(new Set(allFilterableColumns.filter((c) => !next.has(c.id)).map((c) => c.id)));
            }}
            triggerLabel="Columns"
            triggerIcon={Columns3}
            defaultValue={new Set(allFilterableColumns.map((c) => c.id))}
            formatSubtitle={(checked, total) => `${checked} of ${total} shown`}
            selectAllLabel="Show all"
            deselectAllLabel="Hide all"
          />
        ) : undefined
      }
      scrollable
      emptyState={
        <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-16 tw:gap-2">
          <Search size={24} className="tw:text-muted-foreground" />
          <Text variant="body-md">No matching results found</Text>
        </div>
      }
    />
  );
};
